import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Card, Radio } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { getRefrigerantTrackedAppliances } from '../../utils/RefrigerantUtils';
import AssetRefrigerantTrackingPage from '../assets_detail_page/AssetRefrigerantTrackingPage';

/**
 * Refrigerant Tracking, on a work order.
 *
 * A tech reading a work order for an appliance in the 608 programme needs its servicing record in
 * front of them, and needs the event they log to be attributed to the work order they are standing
 * in — which is why `workOrder` is threaded through to the log drawer rather than left to a picker.
 *
 * This is deliberately a thin wrapper around the asset's own Refrigerant Tracking screen rather than a
 * second rendering of the same compliance numbers: two screens deriving a regulated leak rate would
 * eventually disagree.
 *
 * A work order carries one primary asset plus zero or more sub-assets, and any of them can be enrolled
 * independently, so this picks between the enrolled ones. One appliance renders straight through with
 * no picker at all — which is the overwhelmingly common case.
 *
 * No `withUserType` here: the wrapped screen reads the persona out of the session itself, and it builds
 * every asset-scoped link from it.
 */
const WorkOrderRefrigerantTrackingPage: FC<any> = ({ workOrder }) => {
	const appliances = useMemo(() => getRefrigerantTrackedAppliances(workOrder), [workOrder]);
	const [selectedAssetId, setSelectedAssetId] = useState(null);

	// Not seeded into state: `workOrder` arrives asynchronously, so an initial value captured on the
	// first render would be null forever. Falling back to the first appliance keeps the selection
	// valid even if the work order's assets change under it.
	const selectedAppliance =
		appliances.find((appliance) => nullSafeGet('id', appliance) === selectedAssetId) ||
		appliances[0];

	if (!selectedAppliance) {
		return (
			<div className="flex flex-col p-4">
				<Card>
					<EmptyState
						graphic={null}
						headline={'No refrigerant-tracked appliance on this work order.'}
						body={
							<div style={{ textAlign: 'center', maxWidth: 440 }}>
								Turn on "Uses Refrigerant" on the asset to enrol it — its charge, refrigerant and
								EPA appliance category are what the leak rate is computed from.
							</div>
						}
					/>
				</Card>
			</div>
		);
	}

	return (
		<>
			{appliances.length > 1 ? (
				<div className="px-4 pt-4">
					<Radio.Group
						buttonStyle="solid"
						value={nullSafeGet('id', selectedAppliance)}
						onChange={(event) => setSelectedAssetId(event.target.value)}
					>
						{appliances.map((appliance) => (
							<Radio.Button key={appliance.id} value={appliance.id}>
								{nullSafeGet('name', appliance) || `Asset #${appliance.id}`}
							</Radio.Button>
						))}
					</Radio.Group>
				</div>
			) : null}
			{/*
			  Keyed on the appliance so switching between two of them remounts rather than reusing the
			  previous one's summary state — the ledger and the summary are fetched in an effect keyed on
			  `assetId`, and a stale leak rate rendered against the wrong appliance is a wrong regulated
			  number, not a flicker.
			*/}
			<AssetRefrigerantTrackingPage
				key={nullSafeGet('id', selectedAppliance)}
				assetId={nullSafeGet('id', selectedAppliance)}
				asset={selectedAppliance}
				workOrder={workOrder}
			/>
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	workOrder: getEntityById(nullSafeGet('match.params.id', ownProps), state.work_orders),
});

export default withRouter<any, any>(
	connect(mapStateToProps)(WorkOrderRefrigerantTrackingPage as any)
) as any;
